/**
 * Bandeja de notificaciones internas del admin (tabla `notifications`).
 *
 * Es el otro lado del módulo 13: mientras `notification-admin.model` describe lo
 * que el panel *envía* (plantillas y push), esto describe lo que el admin
 * *recibe*. El backend ya escribe aquí los avisos dirigidos al equipo
 * (`support_ticket_new` a todos los admins, `support_reply_user` al asignado).
 */

/** Valores conocidos de `metadata.type` que emite el backend. */
export type InboxNotificationType =
  | 'support_ticket_new'
  | 'support_reply'
  | 'support_reply_user'
  | 'support_escalated'
  | 'support_assigned'
  | 'support_closed'
  | 'return_approved'
  | 'return_rejected'
  | 'return_resolved'
  | 'payment_refunded'
  | 'purchase_rejected'
  | 'seller_payout'
  | 'part_purchased'
  | 'shipment_created'
  | 'shipping_late'
  | 'order_cancelled_no_shipment'
  | 'delivery_confirmed'
  | 'rating_received'
  | 'seller_application_approved'
  | 'account_suspended'
  | 'account_activated'
  | 'offer_created'
  | 'broadcast';

/** `metadata_json` de la notificación; cada tipo agrega su id de contexto. */
export interface InboxNotificationMetadata {
  type?: InboxNotificationType | string;
  ticketId?: number;
  ticketType?: string;
  returnId?: number;
  paymentId?: number;
  refundId?: number;
  applicationId?: number;
  transferId?: string;
  audience?: string;
  [key: string]: unknown;
}

/** Autor de la acción que originó el aviso (puede no existir). */
export interface InboxNotificationActor {
  id?: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  roleId?: number;
  userPhoto?: string | null;
}

/** Fila de `GET /notifications?userId=`. */
export interface InboxNotification {
  id: number;
  recipientUserId?: number;
  actorUserId?: number | null;
  auctionId?: number | null;
  requestedPartId?: number | null;
  offerId?: number | null;
  title?: string | null;
  message?: string | null;
  metadata?: InboxNotificationMetadata | string | null;
  notificationDate?: string | null;
  isRead?: boolean;
  readAt?: string | null;
  status?: number;
  createdAt?: string | null;
  actor?: InboxNotificationActor | null;
  [key: string]: unknown;
}

/** `metadata` llega como objeto o como JSON crudo según la ruta del backend. */
export function notificationMetadata(n: InboxNotification): InboxNotificationMetadata {
  const meta = n.metadata;
  if (!meta) return {};
  if (typeof meta === 'string') {
    try {
      const parsed: unknown = JSON.parse(meta);
      return parsed && typeof parsed === 'object' ? (parsed as InboxNotificationMetadata) : {};
    } catch {
      return {};
    }
  }
  return meta;
}

export function notificationType(n: InboxNotification): string {
  return String(notificationMetadata(n).type ?? '');
}

/** Nombre del icono del aviso (ver `icon-set.ts`). */
export function notificationIcon(n: InboxNotification): string {
  const type = notificationType(n);
  if (type.startsWith('support_')) return 'ticket';
  if (type.startsWith('return_')) return 'package';
  if (type.startsWith('account_')) return 'user';

  const icons: Record<string, string> = {
    payment_refunded: 'undo',
    purchase_rejected: 'undo',
    seller_payout: 'bank',
    part_purchased: 'banknote',
    shipment_created: 'truck',
    shipping_late: 'clock',
    order_cancelled_no_shipment: 'ban',
    delivery_confirmed: 'check-circle',
    rating_received: 'star',
    seller_application_approved: 'store',
    offer_created: 'tag',
    broadcast: 'megaphone',
  };
  return icons[type] ?? 'bell';
}

/** Tono del avatar del aviso; resalta lo que exige atención del equipo. */
export function notificationTone(n: InboxNotification): 'default' | 'info' | 'success' | 'warning' | 'danger' {
  const type = notificationType(n);
  const tones: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
    support_ticket_new: 'warning',
    support_reply_user: 'warning',
    support_escalated: 'danger',
    support_assigned: 'warning',
    support_closed: 'success',
    support_reply: 'info',
    return_approved: 'success',
    return_rejected: 'danger',
    return_resolved: 'success',
    payment_refunded: 'warning',
    purchase_rejected: 'danger',
    shipping_late: 'danger',
    order_cancelled_no_shipment: 'danger',
    delivery_confirmed: 'success',
    seller_payout: 'success',
    part_purchased: 'success',
    rating_received: 'info',
    seller_application_approved: 'success',
    account_suspended: 'danger',
    account_activated: 'success',
    broadcast: 'info',
  };
  return tones[type] ?? 'default';
}

/**
 * Destino del aviso dentro del panel.
 *
 * Devuelve `null` cuando no hay pantalla a la que llevar al admin (el aviso se
 * queda como informativo y solo se marca leído).
 */
export function notificationLink(n: InboxNotification): (string | number)[] | null {
  const meta = notificationMetadata(n);
  const type = String(meta.type ?? '');
  const paymentId = Number(meta.paymentId) || 0;

  if (type.startsWith('support_')) {
    const ticketId = Number(meta.ticketId) || 0;
    return ticketId ? ['/support/tickets', ticketId] : ['/support/tickets'];
  }
  if (type === 'claim_update' || type.startsWith('return_')) {
    const claimId = Number(meta['claimId'] ?? meta['returnId']) || 0;
    return claimId ? ['/support/returns', claimId] : ['/support/returns'];
  }

  switch (type) {
    case 'payment_refunded':
    case 'purchase_rejected':
      return ['/payments/refunds'];
    case 'seller_payout':
      return ['/payments/payouts'];
    case 'part_purchased':
    case 'shipment_created':
    case 'shipping_late':
    case 'order_cancelled_no_shipment':
    case 'delivery_confirmed':
      return paymentId ? ['/payments', paymentId] : ['/payments'];
    case 'rating_received':
      return ['/ratings/reviews'];
    case 'seller_application_approved': {
      const applicationId = Number(meta.applicationId) || 0;
      return applicationId ? ['/sellers/applications', applicationId] : ['/sellers'];
    }
    case 'account_suspended':
    case 'account_activated':
      return ['/users'];
    case 'broadcast':
      return ['/notifications'];
    default: {
      const auctionId = Number(n.auctionId) || 0;
      return auctionId ? ['/catalog/auctions', auctionId] : null;
    }
  }
}

/** Antigüedad legible («hace 5 min»); se recalcula en cada refresco de la bandeja. */
export function notificationAgo(n: InboxNotification, now: number = Date.now()): string {
  const raw = n.notificationDate ?? n.createdAt;
  if (!raw) return '';

  // El backend guarda UTC sin zona ('2026-07-27 18:04:11'): se normaliza a ISO.
  const iso = typeof raw === 'string' && !raw.includes('T') ? `${raw.replace(' ', 'T')}Z` : raw;
  const date = new Date(iso);
  const time = date.getTime();
  if (Number.isNaN(time)) return '';

  const seconds = Math.max(0, Math.round((now - time) / 1000));
  if (seconds < 60) return 'hace un momento';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;

  const days = Math.round(hours / 24);
  if (days < 7) return `hace ${days} d`;

  return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}
